"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

/** A disclosure keeps staff-only utilities available without crowding the header. */
export function WorkspaceTools({ children, modeLabel }: { children: ReactNode; modeLabel?: string }) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      // Inner utility menus handle Escape first and restore their own trigger.
      if (event.key === "Escape" && !container.current?.querySelector('[role="menu"]')) {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div ref={container} className="relative" onBlur={(event) => {
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
    }}>
      <button ref={trigger} data-tour="workspace-tools" type="button" aria-expanded={open} aria-controls="workspace-tools" onClick={() => setOpen(!open)} className={`flex min-h-10 items-center gap-2 rounded-md px-3 text-sm font-semibold transition-colors ${modeLabel ? "bg-spark-yellow-tint text-spark-yellow-text" : "text-spark-navigation-text hover:bg-spark-navigation-hover"}`}>
        Workspace tools{modeLabel && <span className="text-xs">· {modeLabel}</span>}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && <div id="workspace-tools" className="spark-menu-surface absolute right-0 top-full z-[70] mt-2 flex w-72 flex-wrap items-center gap-3 rounded-lg border border-spark-hairline bg-spark-surface p-4 shadow-[var(--shadow-card-hover)]">{children}</div>}
    </div>
  );
}
